import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaExternalLinkAlt,
  FaGraduationCap,
  FaCode,
  FaRocket,
  FaUsers,
} from "react-icons/fa";
import contentConfig from "../content.config.json";
import projectsData from "../projects.json";
import "../Styles/Home.css";

const Home = () => {
  const { hero, about, founders } = contentConfig.home;

  const highlightIconMap = {
    code: FaCode,
    rocket: FaRocket,
    users: FaUsers,
  };

  const featuredProjects = projectsData.categories
    .find((c) => c.id === "applications")
    ?.items.slice(0, 3);

  return (
    <div className="home-container">
      {/* Hero */}
      {hero && (
        <section className="hero-section">
          <div className="hero-background" aria-hidden="true">
            <div className="hero-grid" />
            <div className="hero-glow hero-glow--one" />
            <div className="hero-glow hero-glow--two" />
          </div>

          <div className="hero-content">
            {hero.showLogo && (
              <img
                src="/images/logo-mark-light.png"
                alt="Pip Bros"
                className="hero-logo"
                width="360"
                height="216"
                fetchpriority="high"
                loading="eager"
                decoding="async"
              />
            )}
            {hero.title && <h1 className="hero-title">{hero.title}</h1>}
            {hero.tagline && <p className="hero-tagline">{hero.tagline}</p>}
            {hero.subtitle && <p className="hero-subtitle">{hero.subtitle}</p>}

            <div className="hero-cta-group">
              {hero.ctaText && hero.ctaLink && (
                <Link to={hero.ctaLink} className="btn btn-primary">
                  {hero.ctaText} <FaArrowRight />
                </Link>
              )}
              {hero.secondaryCtaText && hero.secondaryCtaLink && (
                <Link to={hero.secondaryCtaLink} className="btn btn-ghost">
                  {hero.secondaryCtaText}
                </Link>
              )}
            </div>

            <dl className="hero-stats" aria-label="At a glance">
              <div className="hero-stat">
                <dt>Cross-platform</dt>
                <dd>iOS · Android · Web</dd>
              </div>
              <div className="hero-stat">
                <dt>Cloud-native</dt>
                <dd>AWS · Serverless</dd>
              </div>
              <div className="hero-stat">
                <dt>AI-powered</dt>
                <dd>Chat · Agents</dd>
              </div>
              <div className="hero-stat">
                <dt>Blockchain</dt>
                <dd>Smart Contracts · NFT · DeFi</dd>
              </div>
            </dl>
          </div>
        </section>
      )}

      {/* About */}
      {about && (
        <section className="about-section">
          <div className="section-container">
            <div className="section-intro">
              {about.badge && <span className="section-badge">{about.badge}</span>}
              {about.title && <h2 className="section-title">{about.title}</h2>}
              {about.description && (
                <p className="section-lead">{about.description}</p>
              )}
            </div>

            {about.highlights && about.highlights.length > 0 && (
              <div className="highlight-grid">
                {about.highlights.map((highlight, index) => {
                  const Icon = highlightIconMap[highlight.icon];
                  return (
                    <article key={index} className="highlight-card">
                      <div className="highlight-icon">{Icon && <Icon />}</div>
                      <h3 className="highlight-title">{highlight.title}</h3>
                      <p className="highlight-description">
                        {highlight.description}
                      </p>
                    </article>
                  );
                })}
              </div>
            )}

            {about.ctaText && about.ctaLink && (
              <div className="section-cta">
                <Link to={about.ctaLink} className="btn btn-outline">
                  {about.ctaText} <FaArrowRight />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Projects Preview */}
      {featuredProjects && featuredProjects.length > 0 && (
        <section className="featured-section">
          <div className="section-container">
            <div className="section-intro section-intro--row">
              <div>
                <span className="section-badge">Selected Work</span>
                <h2 className="section-title">Recent platforms.</h2>
              </div>
              <Link to="/projects" className="section-link">
                View all projects <FaArrowRight />
              </Link>
            </div>

            <div className="featured-grid">
              {featuredProjects.map((project) => (
                <Link
                  key={project.slug}
                  to="/projects"
                  className="featured-card"
                >
                  {project.images && project.images[0] && (
                    <div className="featured-card-media">
                      <img src={project.images[0]} alt={project.name} loading="lazy" />
                    </div>
                  )}
                  <div className="featured-card-body">
                    <h3>{project.name}</h3>
                    <p>{project.tagline}</p>
                    <span className="featured-card-cta">
                      View project <FaArrowRight />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Founders */}
      {founders && founders.length > 0 && (
        <section className="founders-section">
          <div className="section-container">
            <div className="section-intro">
              <span className="section-badge">The Team</span>
              <h2 className="section-title">Meet the founders.</h2>
            </div>

            <div className="founders-grid">
              {founders.map((founder, index) => (
                <article key={index} className="founder-card">
                  {founder.imagePath && (
                    <div className="founder-image-wrapper">
                      <img
                        src={founder.imagePath}
                        alt={founder.name}
                        className="founder-image"
                      />
                    </div>
                  )}
                  <div className="founder-info">
                    {founder.name && (
                      <h3 className="founder-name">{founder.name}</h3>
                    )}
                    {founder.education && (
                      <div className="founder-education">
                        <FaGraduationCap />
                        <span>{founder.education}</span>
                      </div>
                    )}
                    {founder.bio && (
                      <p className="founder-bio">{founder.bio}</p>
                    )}
                    {founder.website && (
                      <a
                        href={founder.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="founder-website"
                      >
                        View Portfolio <FaExternalLinkAlt />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
